import { SpotToast } from "@/components/SpotToast";
import { createFlowDefinition } from "@/components/Spotflow/Editor/useEditorControls";
import { NodeDefinition, NodesService, WorkflowService } from "@/generated";
import { IODefinition, NoxusReactflowNode } from "@/types/spotflow";
import { UseFormReturn } from "react-hook-form";
import { Edge } from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";

export const GROUP_PADDING = 40;

export const getBoundingBox = (nodes: NoxusReactflowNode[]) => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const node of nodes) {
    const w = node.measured?.width ?? node.width ?? 200;
    const h = node.measured?.height ?? node.height ?? 100;
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + w);
    maxY = Math.max(maxY, node.position.y + h);
  }
  return { minX, minY, maxX, maxY };
};

export const classifyEdges = (edges: Edge[], nodeIds: Set<string>) => ({
  incoming: edges.filter(
    (e) => nodeIds.has(e.target) && !nodeIds.has(e.source)
  ),
  outgoing: edges.filter(
    (e) => nodeIds.has(e.source) && !nodeIds.has(e.target)
  ),
  internal: edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target)),
});

/** Get the data type of a connector from a node's IO definitions. */
export const getConnectorDataType = (
  node: NoxusReactflowNode | undefined,
  handleName: string,
  direction: "inputs" | "outputs"
): string => {
  const connectors = node?.data?.config?.[direction] as
    | IODefinition[]
    | undefined;
  if (!connectors) return "str";

  const [connectorName, key] = handleName.includes("::")
    ? handleName.split("::")
    : [handleName, undefined];

  const connector = connectors.find((c) => c.name === connectorName);
  if (!connector) return "str";

  if (
    connector.type === "variable_type_size_connector" &&
    key &&
    connector.type_definitions
  ) {
    return connector.type_definitions[key]?.data_type || "str";
  }

  return connector.definition?.data_type || "str";
};

/**
 * Shared logic for converting a set of nodes into a subflow workflow
 * and replacing them with an InvokeSubflowNode.
 */
export async function buildSubflowAndInvokeNode(args: {
  nodesToExtract: NoxusReactflowNode[];
  incomingEdges: Edge[];
  outgoingEdges: Edge[];
  internalEdges: Edge[];
  name: string;
  invokePosition: { x: number; y: number };
  form: UseFormReturn;
  spotGroupId: string;
  nodesDefinition: NodeDefinition[] | undefined;
}): Promise<{
  invokeNode: NoxusReactflowNode;
  newEdges: Edge[];
  boundaryEdgeIds: Set<string>;
} | null> {
  const {
    nodesToExtract,
    incomingEdges,
    outgoingEdges,
    internalEdges,
    name,
    invokePosition,
    form,
    spotGroupId,
    nodesDefinition,
  } = args;

  const inputNodeDef = nodesDefinition?.find((n) => n.type === "InputNode");
  const outputNodeDef = nodesDefinition?.find((n) => n.type === "OutputNode");

  const subflowNodes: NoxusReactflowNode[] = [...nodesToExtract];
  const subflowEdges: Edge[] = [];

  // Create Input nodes for each unique incoming edge
  const inputLabelMap: Map<string, { nodeId: string; label: string }> =
    new Map();
  let inputCounter = 1;

  for (const edge of incomingEdges) {
    const targetHandle = edge.targetHandle || "input";
    const mapKey = `${edge.target}::${targetHandle}`;

    if (!inputLabelMap.has(mapKey)) {
      const inputNodeId = uuidv4();
      const label = `Input ${inputCounter}`;
      inputCounter++;

      const targetNode = nodesToExtract.find((n) => n.id === edge.target);
      const connectorType = getConnectorDataType(
        targetNode,
        targetHandle,
        "inputs"
      );
      subflowNodes.push({
        id: inputNodeId,
        type: "baseNode",
        position: {
          x: (targetNode?.position.x ?? 0) - 300,
          y: targetNode?.position.y ?? 0,
        },
        data: {
          config: { ...structuredClone(inputNodeDef) },
          name: label,
          type: "InputNode",
        },
      });
      form.setValue(`${inputNodeId}.label`, label);
      form.setValue(`${inputNodeId}.type`, connectorType);

      subflowEdges.push({
        id: uuidv4(),
        source: inputNodeId,
        target: edge.target,
        sourceHandle: "output",
        targetHandle: edge.targetHandle,
        type: "customEdge",
      });

      inputLabelMap.set(mapKey, { nodeId: inputNodeId, label });
    }
  }

  // Create Output nodes for each unique outgoing edge
  const outputLabelMap: Map<string, { nodeId: string; label: string }> =
    new Map();
  let outputCounter = 1;

  for (const edge of outgoingEdges) {
    const sourceHandle = edge.sourceHandle || "output";
    const mapKey = `${edge.source}::${sourceHandle}`;

    if (!outputLabelMap.has(mapKey)) {
      const outputNodeId = uuidv4();
      const label = `Output ${outputCounter}`;
      outputCounter++;

      const sourceNode = nodesToExtract.find((n) => n.id === edge.source);
      const connectorType = getConnectorDataType(
        sourceNode,
        sourceHandle,
        "outputs"
      );
      subflowNodes.push({
        id: outputNodeId,
        type: "baseNode",
        position: {
          x: (sourceNode?.position.x ?? 0) + 300,
          y: sourceNode?.position.y ?? 0,
        },
        data: {
          config: { ...structuredClone(outputNodeDef) },
          name: label,
          type: "OutputNode",
        },
      });
      form.setValue(`${outputNodeId}.label`, label);
      form.setValue(`${outputNodeId}.type`, connectorType);

      subflowEdges.push({
        id: uuidv4(),
        source: edge.source,
        target: outputNodeId,
        sourceHandle: edge.sourceHandle,
        targetHandle: "input",
        type: "customEdge",
      });

      outputLabelMap.set(mapKey, { nodeId: outputNodeId, label });
    }
  }

  subflowEdges.push(...internalEdges);

  // Build subflow definition and create workflow
  const formValues = form.getValues();
  const subflowDefinition = createFlowDefinition(
    subflowNodes,
    subflowEdges,
    formValues
  ).mainFlowDefinition;

  let newWorkflowId: string;
  try {
    const result = await WorkflowService.addWorkflow({
      path: { group_id: spotGroupId },
      body: { type: "flow", name, definition: subflowDefinition },
    });
    newWorkflowId = result.data.id;
  } catch (e) {
    console.error(e);
    SpotToast.error("Failed to create subflow workflow");
    return null;
  }

  // Build InvokeSubflowNode
  try {
    const subflowConfig = await NodesService.getNodeConfig({
      path: { group_id: spotGroupId, node_id: "InvokeSubflowNode" },
      body: { workflow_id: newWorkflowId },
    });

    const invokeNodeDef = nodesDefinition?.find(
      (n) => n.type === "InvokeSubflowNode"
    );
    const invokeNodeId = uuidv4();
    const invokeNode: NoxusReactflowNode = {
      id: invokeNodeId,
      type: "baseNode",
      position: invokePosition,
      data: {
        config: {
          ...structuredClone(invokeNodeDef),
          config: {
            ...structuredClone(invokeNodeDef)?.config,
            ...(subflowConfig.data as any).config,
          },
          inputs: (subflowConfig.data as any).inputs,
          outputs: (subflowConfig.data as any).outputs,
          subflow_config: { workflow_id: newWorkflowId, workflow_name: name },
        },
        name,
        type: "InvokeSubflowNode",
        plugin_name: invokeNodeDef?.plugin_name,
      },
    };

    form.setValue(`${invokeNodeId}.workflow_id`, newWorkflowId);

    const invokeInputs = (subflowConfig.data as any).inputs as IODefinition[];
    const invokeOutputs = (subflowConfig.data as any).outputs as IODefinition[];

    // Re-wire incoming edges to InvokeSubflowNode
    const newEdges: Edge[] = [];
    const processedIncoming = new Set<string>();

    for (const edge of incomingEdges) {
      const targetHandle = edge.targetHandle || "input";
      const mapKey = `${edge.target}::${targetHandle}`;
      const inputInfo = inputLabelMap.get(mapKey);
      if (!inputInfo) continue;

      let invokeTargetHandle = "inputs";
      for (const inp of invokeInputs) {
        if (inp.keys?.includes(inputInfo.label)) {
          invokeTargetHandle = `${inp.name}::${inputInfo.label}`;
          break;
        }
      }

      const edgeKey = `${edge.source}::${edge.sourceHandle}::${invokeTargetHandle}`;
      if (!processedIncoming.has(edgeKey)) {
        processedIncoming.add(edgeKey);
        newEdges.push({
          id: uuidv4(),
          source: edge.source,
          target: invokeNodeId,
          sourceHandle: edge.sourceHandle,
          targetHandle: invokeTargetHandle,
          type: "customEdge",
        });
      }
    }

    // Re-wire outgoing edges from InvokeSubflowNode
    const processedOutgoing = new Set<string>();

    for (const edge of outgoingEdges) {
      const sourceHandle = edge.sourceHandle || "output";
      const mapKey = `${edge.source}::${sourceHandle}`;
      const outputInfo = outputLabelMap.get(mapKey);
      if (!outputInfo) continue;

      let invokeSourceHandle = "outputs";
      for (const out of invokeOutputs) {
        if (out.keys?.includes(outputInfo.label)) {
          invokeSourceHandle = `${out.name}::${outputInfo.label}`;
          break;
        }
      }

      const edgeKey = `${invokeSourceHandle}::${edge.target}::${edge.targetHandle}`;
      if (!processedOutgoing.has(edgeKey)) {
        processedOutgoing.add(edgeKey);
        newEdges.push({
          id: uuidv4(),
          source: invokeNodeId,
          target: edge.target,
          sourceHandle: invokeSourceHandle,
          targetHandle: edge.targetHandle,
          type: "customEdge",
        });
      }
    }

    const boundaryEdgeIds = new Set([
      ...incomingEdges.map((e) => e.id),
      ...outgoingEdges.map((e) => e.id),
      ...internalEdges.map((e) => e.id),
    ]);

    return { invokeNode, newEdges, boundaryEdgeIds };
  } catch (e) {
    console.error(e);
    SpotToast.error("Failed to configure subflow node");
    return null;
  }
}
